'use client';

import Image, { ImageProps } from 'next/image';
import React, { useState } from 'react';
import {
  DEFAULT_PLACEHOLDER_IMAGE,
  toProxyUploadUrl,
} from '@/lib/utils/upload-url';

interface AppImageProps {
    src: string;
    alt: string;
    width?: number;
    height?: number;
    className?: string;
    priority?: boolean;
    quality?: number;
    placeholder?: 'blur' | 'empty';
    blurDataURL?: string;
    fill?: boolean;
    sizes?: string;
    onClick?: () => void;
    fallbackSrc?: string;
    imageProps?: Omit<ImageProps, 'src' | 'alt'>;
}

function AppImage({
    src,
    alt,
    width,
    height,
    className = '',
    priority = false,
    quality = 75,
    placeholder = 'empty',
    blurDataURL,
    fill = false,
    sizes,
    onClick,
    fallbackSrc = DEFAULT_PLACEHOLDER_IMAGE,
    imageProps,
}: AppImageProps) {
    const resolvedFallback = toProxyUploadUrl(fallbackSrc);
    const normalizedSrc = toProxyUploadUrl(src || resolvedFallback);
    const [failedSrc, setFailedSrc] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const imageSrc =
        failedSrc === normalizedSrc ? resolvedFallback : normalizedSrc;

    const isExternal =
        imageSrc.startsWith('http://') || imageSrc.startsWith('https://');

    const handleError = () => {
        if (imageSrc !== resolvedFallback) {
            setFailedSrc(normalizedSrc);
        }
        setIsLoading(false);
    };

    const handleLoad = () => {
        setIsLoading(false);
    };

    const commonClassName = `${className} ${isLoading ? 'bg-gray-200' : ''} ${onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`;

    // Always use Next.js Image; external URLs are handled via `unoptimized`
    const baseImageProps: ImageProps = {
        src: imageSrc,
        alt,
        className: commonClassName,
        priority,
        quality,
        placeholder,
        blurDataURL,
        unoptimized: isExternal,
        onError: handleError,
        onLoad: () => handleLoad(),
        onClick,
        ...(imageProps || {}),
    };

    if (fill) {
        return (
            <div className={`relative ${className}`}>
                <Image
                    {...baseImageProps}
                    alt={alt}
                    fill
                    sizes={sizes || '100vw'}
                />
            </div>
        );
    }

    return (
        <Image
            {...baseImageProps}
            alt={alt}
            width={width || 400}
            height={height || 300}
        />
    );
}

export default AppImage;